import { useCallback, useMemo, useState } from 'react';
import useAgentsChats from './useAgentsChats';
import useAiAgents from './useAiAgents';
import { usePermissions } from '../../permissions/usePermissions';
import useConfirm from '../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { AI_AGENTS_INFO, AiAgent } from './aiAgentsConsts';
import ConfirmDestructiveDialog from '../common/dialogs/ConfirmDestructiveDialog';
import { AI_AGENTS_EVENTS } from '../../services/events';
import { sendBiEvent } from '../../services/bi';
import { colors } from '@sweep-io/sweep-design';
import AgentSettingsDialog, { SettingSection } from './agentSettings/AgentSettingsDialog';
import RenameAgentDialog from './RenameAgentDialog';
import { useAgentSettingsContext } from './AgentSettingsContext';
import { PromptsLibraryDialog } from './promptsLibrary__deprecated/PromptsLibraryDialog';
import { PromptsLibraryV2Dialog } from './promptsLibrary/PromptsLibraryV2Dialog';
import { useUnhappyStates } from '../pages/environments/useUnhappyStates';
import { AgentConfigurationsDialog } from './agent-selection/AgentConfigurationsDialog';
import { AgentType } from '@server/ai';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
export interface AgentAction {
  onClick: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
  color?: string;
}

export interface AgentActionsOptions {
  origin?: 'header_menu' | 'button' | 'card_menu' | 'sidebar' | 'agent_name_button' | 'agent_card';
  mode?: 'expanded' | 'collapsed';
}

export const useAgentActions = (
  agent: AiAgent | null | undefined,
  options: AgentActionsOptions = {},
) => {
  const { isSettingsOpen, initialSection, openSettings, closeSettings } = useAgentSettingsContext();
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isPromptsLibraryDialogOpen, setIsPromptsLibraryDialogOpen] = useState(false);
  const [isConfigurationsDialogOpen, setIsConfigurationsDialogOpen] = useState(false);
  const { startNewChat, onConfirmMessage } = useAgentsChats();
  const { renameAgent, deleteAgent } = useAiAgents();
  const { openConfirm, onConfirm, onCancel } = useConfirm();
  const { isInitialFetch } = useUnhappyStates();
  const { promptsLibraryV2 } = useFeatureToggle();

  const { origin = 'header_menu', mode = 'expanded' } = options;

  const agentInfo = agent ? AI_AGENTS_INFO[agent?.type] : undefined;
  const isBestPracticesSupported = agentInfo?.isSupportBestPractices;
  const isSingletonAgent = !agentInfo?.createEntryPoint.isAllowed;

  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { evaluatePermissions } = usePermissions();
  const [isAllowedToEditAgent] = evaluatePermissions([{ permission: 'edit:ai-agent', crmOrgId: crmOrgId ?? '' }]);
  const [isAllowedToDeleteAgent] = evaluatePermissions([{ permission: 'delete:ai-agent', crmOrgId: crmOrgId ?? '' }]);

  const startChatWithAgent = useCallback(() => {
    if (agent) {
      startNewChat({
        agentId: agent.id,
        agentType: agent.type,
        biEventProps: {
          origin,
          mode,
        },
      });
    }
  }, [agent, startNewChat, origin, mode]);

  const openRenameDialog = useCallback(() => setIsRenameDialogOpen(true), []);

  const closeRenameDialog = useCallback(() => setIsRenameDialogOpen(false), []);

  const handleRename = useCallback(
    async (name: string) => {
      if (agent) {
        await renameAgent(agent.id, name);
        setIsRenameDialogOpen(false);
      }
    },
    [agent, renameAgent],
  );

  const handleDelete = useCallback(async () => {
    if (!agent) return;

    const isConfirmed = await openConfirm(
      <ConfirmDestructiveDialog
        onConfirm={onConfirm}
        onClose={onCancel}
        open={true}
        confirmText="Delete"
        title={`Are you sure you want to delete "${agent.name}" agent?`}
      >
        By deleting "{agent.name}" you'll lose all your conversations with it.
      </ConfirmDestructiveDialog>,
    );
    if (isConfirmed) {
      sendBiEvent({ name: AI_AGENTS_EVENTS.deleteAgent });
      await deleteAgent(agent.id);
    }
  }, [agent, deleteAgent, onCancel, onConfirm, openConfirm]);

  const newChatAction: AgentAction = {
    onClick: startChatWithAgent,
    disabled: false,
  };

  const settingsAction: AgentAction = {
    onClick: () => openSettings(SettingSection.Instructions),
    disabled: false,
  };

  const bestPracticesAction: AgentAction | null = isBestPracticesSupported
    ? {
      onClick: () => openSettings(SettingSection.BestPractices),
      disabled: false,
    }
    : null;

  const renameAction: AgentAction = {
    onClick: openRenameDialog,
    disabled: !isAllowedToEditAgent,
    disabledTooltip: 'To rename this agent, please contact your admin.',
  };

  const deleteAction: AgentAction | null = isSingletonAgent
    ? null
    : {
      onClick: handleDelete,
      disabled: !isAllowedToDeleteAgent,
      disabledTooltip: 'To delete this agent, please contact your admin.',
      color: colors.blush[500],
    };

  const settingsDialog = useMemo(
    () =>
      isSettingsOpen ? (
        <AgentSettingsDialog
          isOpened={true}
          closeDialog={closeSettings}
          initialSection={initialSection}
        />
      ) : null,
    [isSettingsOpen, closeSettings, initialSection],
  );

  const renameDialog = useMemo(
    () =>
      isRenameDialogOpen ? (
        <RenameAgentDialog
          isOpen={true}
          closeDialog={closeRenameDialog}
          initialName={agent?.name || ''}
          onConfirm={handleRename}
        />
      ) : null,
    [isRenameDialogOpen, closeRenameDialog, agent?.name, handleRename],
  );

  const openPromptsLibraryDialog = useCallback(() => {
    sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryOpen });
    setIsPromptsLibraryDialogOpen(true);
  }, []);

  const closePromptsLibraryDialog = useCallback(() => setIsPromptsLibraryDialogOpen(false), []);

  const openConfigurationsDialog = useCallback(() => setIsConfigurationsDialogOpen(true), []);
  const closeConfigurationsDialog = useCallback(() => setIsConfigurationsDialogOpen(false), []);

  const handlePromptClick = useCallback(
    async (prompt: string) => {
      if (!agent) {
        return;
      }
      sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryPromptClick });
      //no "await" because the product is to navigate to the new chat immediately, without waiting for BE response
      onConfirmMessage({
        messageStr: prompt,
        agentId: agent.id,
        agentType: agent.type,
        replyTo: undefined,
        isFirstMessage: true,
        dontSendToServer: isInitialFetch,
      });
      closePromptsLibraryDialog();
    },
    [agent, onConfirmMessage, closePromptsLibraryDialog, isInitialFetch],
  );

  const promptsLibraryDialog = useMemo(
    () =>
      promptsLibraryV2 ? (
        <PromptsLibraryV2Dialog
          open={isPromptsLibraryDialogOpen}
          onClose={closePromptsLibraryDialog}
        />
      ) : (
        <PromptsLibraryDialog
          open={isPromptsLibraryDialogOpen}
          onClose={closePromptsLibraryDialog}
          onPromptClick={handlePromptClick}
        />
      ),
    [isPromptsLibraryDialogOpen, closePromptsLibraryDialog, promptsLibraryV2, handlePromptClick],
  );

  const promptsLibraryAction: AgentAction = {
    onClick: openPromptsLibraryDialog,
    disabled: false,
  };

  // Only show configurations for MonitoringV2 agents
  const configurationsAction: AgentAction | null =
    agent?.type === AgentType.MonitoringV2
      ? {
        onClick: openConfigurationsDialog,
        disabled: !isAllowedToEditAgent,
        disabledTooltip: "To edit the agent's configurations, please contact your admin.",
      }
      : null;

  const configurationsDialog = useMemo(
    () =>
      agent && isConfigurationsDialogOpen ? (
        <AgentConfigurationsDialog
          isOpen={true}
          onClose={closeConfigurationsDialog}
          agent={agent}
        />
      ) : null,
    [isConfigurationsDialogOpen, closeConfigurationsDialog, agent],
  );

  return {
    newChatAction,
    settingsAction,
    bestPracticesAction,
    renameAction,
    deleteAction,
    promptsLibraryAction,
    configurationsAction,
    settingsDialog,
    renameDialog,
    isSettingsOpen,
    agent,
    promptsLibraryDialog,
    configurationsDialog,
  };
};
